import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Form, Input, Upload, Select, Button, message, Row, Col, Collapse } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Block, Confirm, Loading, Report } from "notiflix";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";

import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";

import "./styles.css";
import PdfViewer from "../../../atoms/pdf-viewer";

const { Dragger } = Upload;
const { TextArea } = Input;
const { Panel } = Collapse;

const CreateDASTR = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const [fileList, setFileList] = useState([]);
    const [attachmentBase64, setAttachmentBase64] = useState(null);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [approverOptions, setApproverOptions] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [activeKeys, setActiveKeys] = useState(["1", "2"]);

    const currentSession = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("session_farms")) || null;
        } catch (error) {
            return null;
        }
    }, []);

    const loadApprovers = async () => {
        Loading.dots("Fetching approvers...");
        try {
            const token = await requestToken();
            if (!token) {
                Report.warning("DASTR", "Unable to retrieve access token.", "OK");
                return;
            }
            const response = await API.getEmployeesByBranch(HeaderFarms(token));
            const options = (response?.data || []).map((item) => ({
                value: item.employee_id,
                label: `${item.fullname?.trim?.() || item.fullname} (${item.employee_id}) - ${item.job_level?.trim?.()}`,
            }));
            setApproverOptions(options);
        } catch (error) {
            console.error("loadApprovers", error);
            Report.warning("DASTR", "Failed to load approver list. Please try again.", "OK");
        } finally {
            Loading.remove();
        }
    };

    useEffect(() => {
        loadApprovers();
    }, []);

    const handleBeforeUpload = (file) => {

        if (file.type !== "application/pdf") {
            message.error("Only PDF files are allowed");
            return Upload.LIST_IGNORE;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setAttachmentBase64(event.target.result);
        };
        reader.readAsDataURL(file);

        const fileUrl = URL.createObjectURL(file);
        setPdfPreviewUrl(fileUrl);

        setActiveKeys(prev => prev.includes("3") ? prev : [...prev, "3"]);
        setFileList([
            {
                uid: file.uid,
                name: file.name,
                status: "done",
            },
        ]);

        return false; // prevent auto upload
    };

    const handleRemove = () => {
        setAttachmentBase64(null);
        setPdfPreviewUrl(null);
        setFileList([]);
        setActiveKeys(prev => prev.filter(key => key !== "3"));
    };

    const handleSubmit = async (values) => {
        if (!attachmentBase64) {
            message.error("Please upload a document first");
            return;
        }

        if (!values.approvers || values.approvers.length === 0) {
            message.error("Please select at least one approver");
            return;
        }

        Confirm.show(
            "Important Confirmation",
            "Are you sure? Please make sure your request is correct.",
            "Yes",
            "No",
            () => {
                setSubmitting(true);
                Loading.dots("Processing your request...");

                (async () => {

                    try {

                        const token = await requestToken();

                        if (!token) {
                            Report.warning("DASTR", "Unable to retrieve access token.", "OK");
                            return;
                        }

                        const payload = {
                            description: values.description,
                            document_name: values.document_name,
                            document_id: values.document_id,
                            attachment: attachmentBase64,
                            custom_approval: values.approvers,
                        };

                        if (currentSession?.employee_id) {
                            payload.created_by = currentSession.employee_id;
                        }

                        await API.createDocument(HeaderFarms(token), payload);

                        Report.success("DASTR", "Document submitted successfully", "OK", () =>
                            navigate("/dst/history")
                        );

                        form.resetFields();

                        setFileList([]);
                        setAttachmentBase64(null);
                        setPdfPreviewUrl(null);
                        setActiveKeys(prev => prev.filter(key => key !== "3"));

                    } catch (error) {
                        console.error("createDocument", error);
                        const messageError =
                            error?.response?.data?.error?.[0]?.name || "An error occurred while saving the document";
                        Report.warning("DASTR", messageError, "OK");
                    } finally {
                        Loading.remove();
                        setSubmitting(false);
                    }
                })();
            }
        );
    };

    const onChangeCollapse = (key) => {
        setActiveKeys(key);
    };

    return (
        <>
            <Navbar
                title="Create"
                back={true}
                subtitle="DASTR"
                navigate="/dst/history"
            />
            <div className="container-fluid white-back-cr">
                <Form
                    form={form}
                    layout="vertical"
                    size="small"
                    onFinish={handleSubmit}
                    onFinishFailed={() => message.warning("Please complete the form.")}
                    disabled={submitting}
                >
                    <div className="container p-3" style={{ alignSelf: "center" }}>
                        <div className="row">
                            <div className="col-md-12">
                                <Collapse
                                    className="collapse_sr"
                                    expandIconPosition={"end"}
                                    bordered={true}
                                    activeKey={activeKeys}
                                    onChange={onChangeCollapse}
                                >
                                    <Panel style={{ background: "#fce8b6" }} header={<b>Document Details</b>} key="1">
                                        <Row gutter={10}>
                                            <Col xs={{ span: 24 }} sm={{ span: 12 }} xl={{ span: 12 }}>
                                                <Form.Item
                                                    label="Document Name"
                                                    name="document_name"
                                                    rules={[{ required: true, message: "Please input document name" }]}
                                                >
                                                    <Input placeholder="Enter document name" allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={{ span: 24 }} sm={{ span: 12 }} xl={{ span: 12 }}>
                                                <Form.Item
                                                    label="Document ID"
                                                    name="document_id"
                                                    rules={[{ required: false, message: "Please input document ID" }]}
                                                >
                                                    <Input placeholder="Enter document ID (optional)" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={10}>
                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} xl={{ span: 24 }}>
                                                <Form.Item
                                                    label="Description"
                                                    name="description"
                                                    rules={[{ required: true, message: "Please input description" }]}
                                                >
                                                    <TextArea rows={4} maxLength={500} showCount allowClear placeholder="Write a short description of the document" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={10}>
                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} xl={{ span: 24 }}>
                                                <Form.Item label="Document Attachment (PDF)" required>
                                                    <Dragger beforeUpload={handleBeforeUpload} fileList={fileList} onRemove={handleRemove} accept=".pdf">
                                                        <p className="ant-upload-drag-icon">
                                                            <InboxOutlined />
                                                        </p>
                                                        <p className="ant-upload-text">Click or drag PDF file to this area to upload</p>
                                                        <p className="ant-upload-hint">Maximum 1 file in PDF format.</p>
                                                    </Dragger>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Panel>
                                    {pdfPreviewUrl && (
                                        <Panel style={{ background: "#e6f7ff", marginTop: 10 }} header={<b>PDF Preview</b>} key="3">
                                            <Row>
                                                <Col xs={{ span: 24 }}>
                                                    <PdfViewer fileUrl={pdfPreviewUrl} height={600} />
                                                </Col>
                                            </Row>
                                        </Panel>
                                    )}
                                    <Panel style={{ background: "#fce8b6" }} header={<b>Approval</b>} key="2">
                                        <Row gutter={10}>
                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} xl={{ span: 24 }}>
                                                <Form.Item
                                                    label="Choose Approvers"
                                                    name="approvers"
                                                    rules={[{ required: true, message: "Please select approver(s)" }]}
                                                    extra="Selected approvers will be ordered from top to bottom"
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Search and select approver(s)"
                                                        options={approverOptions}
                                                        showSearch
                                                        size="middle"
                                                        optionFilterProp="label"
                                                        loading={approverOptions.length === 0}
                                                        disabled={approverOptions.length === 0}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Panel>
                                </Collapse>
                            </div>
                        </div>
                    </div>
                    <Row className="justify-content-center">
                        <Col>
                            <Form.Item>
                                <Button type="primary" size="large" htmlType="submit" loading={submitting}>
                                    Submit
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form >
            </div >
            <Footer />
        </>
    );
};

export default CreateDASTR;
